import { toaster } from '@/core/ui';
import {
  DeleteQuestionRecordReviewsDocument,
  UpdateQuestionRecordReviewDocument,
} from '@/graphql/gql/graphql';
import { gql, useMutation as useApolloMutation } from '@/libs/apollo/export';

export const useReviewMutations = () => {
  const [updateRecordReviewMutation, { loading: updateLoading }] = useApolloMutation(
    UpdateQuestionRecordReviewDocument,
    {
      update(cache, _, { variables }) {
        if (!variables) return;
        const { id, input } = variables;

        const reviewId = cache.identify({ __typename: 'RecordReview', id });
        if (!reviewId) return;

        if (input.comment !== undefined) {
          cache.writeFragment({
            id: reviewId,
            fragment: gql`
              fragment UpdatedComment on RecordReview {
                comment
              }
            `,
            data: { comment: input.comment },
          });
        }

        if (input.actionedBy !== undefined) {
          cache.writeFragment({
            id: reviewId,
            fragment: gql`
              fragment UpdatedActionedBy on RecordReview {
                actionedBy
              }
            `,
            data: { actionedBy: input.actionedBy },
          });
        }

        if (input.reason !== undefined) {
          cache.writeFragment({
            id: reviewId,
            fragment: gql`
              fragment UpdatedReason on RecordReview {
                reason
              }
            `,
            data: { reason: input.reason },
          });
        }

        if (input.state !== undefined) {
          cache.writeFragment({
            id: reviewId,
            fragment: gql`
              fragment UpdatedState on RecordReview {
                state
              }
            `,
            data: { state: input.state },
          });
        }

        if (input.members !== undefined) {
          cache.writeFragment({
            id: reviewId,
            fragment: gql`
              fragment UpdatedMembers on RecordReview {
                members {
                  userId
                  role
                  user {
                    id
                    firstName
                    displayName
                    username
                  }
                }
              }
            `,
            data: { members: input.members },
          });
        }
      },
    },
  );

  const [deleteRecordReviewsMutation, { loading: deleteLoading }] = useApolloMutation(
    DeleteQuestionRecordReviewsDocument,
    {
      update(cache, _, { variables }) {
        if (!variables) return;
        const { ids = [] } = variables as { ids: string[] };
        if (ids.length === 0) return;

        ids.forEach((id) => {
          cache.evict({
            id: cache.identify({ __typename: 'RecordReview', id }),
          });
        });
      },
    },
  );

  const handleStateChange = async (reviewId: string, val: number | null) => {
    if (!reviewId || val == null) return;

    try {
      await updateRecordReviewMutation({
        variables: { id: reviewId, input: { state: val } },
      });
      toaster.success('State updated');
    } catch (err: any) {
      toaster.error(err?.message || 'Failed to update state');
    }
  };

  const handleActionedByChange = async (reviewId: string, val: number | null) => {
    if (!reviewId || val == null) return;
    try {
      await updateRecordReviewMutation({
        variables: { id: reviewId, input: { actionedBy: val } },
      });
      toaster.success('Actioned by updated');
    } catch (err: any) {
      toaster.error(err?.message || 'Failed to update actioned by');
    }
  };

  const handleMembersChange = async (reviewId: string, keys: any) => {
    if (!reviewId || !keys) return;
    const currentIds = Array.from(keys) as (string | number)[];

    try {
      await updateRecordReviewMutation({
        variables: {
          id: String(reviewId),
          input: {
            members: currentIds.map((userId) => ({ userId: Number(userId), role: 1 })),
          },
        },
      });
      toaster.success('Members updated');
    } catch (err: any) {
      toaster.error(err?.message || 'Failed to update members');
    }
  };

  const handleCommentSave = async (reviewId: string, comment: string) => {
    if (!reviewId) return;
    try {
      await updateRecordReviewMutation({
        variables: {
          id: reviewId,
          input: { comment },
        },
      });
      toaster.success('Comment updated');
    } catch (err: any) {
      toaster.error(err?.message || 'Failed to update comment');
    }
  };

  const handleDelete = async (reviewId: string | number) => {
    try {
      const res = await deleteRecordReviewsMutation({
        variables: { ids: [String(reviewId)] },
      });
      if (res.data?.restricted?.deleteRecordReviews) {
        toaster.success('Review deleted');
      } else {
        toaster.error('Failed to delete review');
      }
    } catch (err: any) {
      toaster.error(err?.message || 'Failed to delete review');
    }
  };

  const handleMultiDelete = async (ids: (string | number)[]) => {
    if (!ids.length) return;
    try {
      const res = await deleteRecordReviewsMutation({
        variables: { ids: ids.map(String) },
      });
      if (res.data?.restricted?.deleteRecordReviews) {
        toaster.success(`${ids.length} review${ids.length > 1 ? 's' : ''} deleted`);
        return true;
      } else {
        toaster.error('Failed to delete selected reviews');
        return false;
      }
    } catch (err: any) {
      toaster.error(err?.message || 'Failed to delete selected reviews');
      return false;
    }
  };

  const handleBulkStateUpdate = async (ids: (string | number)[], state: number) => {
    const tasks = ids.map((id) =>
      updateRecordReviewMutation({
        variables: { id: String(id), input: { state } },
      }),
    );
    try {
      await Promise.all(tasks);
      toaster.success(`State updated to ${state} for ${ids.length} review${ids.length > 1 ? 's' : ''}`);
      return true;
    } catch (err: any) {
      toaster.error(err?.message || 'Failed to update state for selected reviews');
      return false;
    }
  };

  return {
    updateRecordReviewMutation,
    deleteRecordReviewsMutation,
    updateLoading,
    deleteLoading,
    handleStateChange,
    handleActionedByChange,
    handleMembersChange,
    handleCommentSave,
    handleDelete,
    handleMultiDelete,
    handleBulkStateUpdate,
  };
};
